'use client';

import { isAttuneDesktopShell } from '@/lib/desktop-auth-client';
import { integrationSetupReturnPath } from '@/lib/integrations/setup-return';
import { useIntegrationSetupFlow } from './useIntegrationSetupFlow';
import { IntegrationSetupError } from './IntegrationSetupError';
import { IntegrationResourcePicker } from './IntegrationResourcePicker';

import { Badge, Box, Button, HStack, Text, VStack } from '@chakra-ui/react';
import { Check, ExternalLink, Link2, RefreshCw, Unlink, AlertCircle } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from '@/app/auth-provider';
import {
  IntegrationCard,
  IntegrationNotice,
  IntegrationStatusRow,
} from '@/components/shared/integrations/IntegrationCardShell';
import { InlineSpinner } from '@/components/shared/PageLoader';
import { useProject } from '@/contexts/ProjectContext';

interface GSCProperty {
  siteUrl: string;
  permissionLevel: string;
  isSelected: boolean;
  linkedBrandDomain: string | null;
}

interface GSCConnectionStatus {
  connected: boolean;
  needsReconnect?: boolean;
  canManageConnection?: boolean;
  canSelectProperties?: boolean;
  connection?: {
    id: string;
    googleEmail: string;
    status: string;
    errorMessage: string | null;
    connectedAt: string;
    lastUsedAt: string | null;
  };
  properties: Array<{
    id: string;
    siteUrl: string;
    permissionLevel: string | null;
    brandDomain: string | null;
    lastSyncAt: string | null;
    lastSyncStatus: string | null;
  }>;
}

export function GSCIntegration() {
  const { user } = useAuth();
  const { currentProject } = useProject();
  const setup = useIntegrationSetupFlow(currentProject?.id);

  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [status, setStatus] = useState<GSCConnectionStatus | null>(null);

  // Property picker state
  const [showPropertyPicker, setShowPropertyPicker] = useState(false);
  const [properties, setProperties] = useState<GSCProperty[]>([]);
  const [loadingProperties, setLoadingProperties] = useState(false);
  const [selectingProperty, setSelectingProperty] = useState<string | null>(null);

  const brandDomain =
    currentProject?.brand_settings?.website_domain ||
    (currentProject?.brand_settings?.website_url
      ? (() => {
          try {
            return new URL(currentProject.brand_settings.website_url).hostname.replace(
              /^www\./,
              ''
            );
          } catch {
            return null;
          }
        })()
      : null);

  const fetchStatus = useCallback(async () => {
    if (!user) return;

    try {
      const token = await user.getIdToken();
      const res = await fetch(
        `/api/integrations/gsc/status?${new URLSearchParams({ ...(brandDomain ? { brand_domain: brandDomain } : {}), ...(currentProject?.id ? { projectId: currentProject.id } : {}) })}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.ok) {
        const data = await res.json();
        setStatus(data);
      }
    } catch (error) {
      console.error('Failed to fetch GSC status:', error);
    } finally {
      setLoading(false);
    }
  }, [user, brandDomain, currentProject?.id]);

  const fetchProperties = useCallback(async () => {
    if (!user) return;

    setLoadingProperties(true);
    try {
      const token = await user.getIdToken();
      const res = await fetch(
        `/api/integrations/gsc/properties${currentProject?.id ? `?projectId=${encodeURIComponent(currentProject.id)}` : ''}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!res.ok) throw new Error('Properties could not be loaded.');

      if (res.ok) {
        const data = await res.json();
        setProperties(data.properties || []);
      }
    } catch (error) {
      console.error('Failed to fetch properties:', error);
      setup.reportError('Failed to load Search Console properties.');
    } finally {
      setLoadingProperties(false);
    }
  }, [user, currentProject?.id]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Check for OAuth callback params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const gscSuccess = params.get('gsc_success');
    const gscError = params.get('gsc_error');

    // Helper to clean GSC-specific params while preserving others (like tab)
    const cleanGscParams = () => {
      const cleanParams = new URLSearchParams(window.location.search);
      cleanParams.delete('gsc_success');
      cleanParams.delete('gsc_error');
      cleanParams.delete('gsc_connection');
      cleanParams.delete('gsc_properties');
      const newUrl = cleanParams.toString()
        ? `${window.location.pathname}?${cleanParams.toString()}`
        : window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    };

    if (gscSuccess === 'true') {
      toast.success('Google Search Console connected successfully!');
      // Clean GSC params from URL (preserve tab param)
      cleanGscParams();
      // Refresh status, fetch properties, and show property picker
      fetchStatus().then(() => {
        setShowPropertyPicker(true);
        fetchProperties();
      });
    } else if (gscError) {
      const errorMessages: Record<string, string> = {
        denied: 'Google Search Console connection was cancelled.',
        expired: 'Authorization expired. Please try again.',
        invalid: 'Invalid authorization request.',
        invalid_user: 'User verification failed.',
        server_error: 'An error occurred. Please try again.',
      };
      setup.reportError(errorMessages[gscError] || 'Failed to connect Google Search Console.');
      cleanGscParams();
    }
  }, [fetchStatus, fetchProperties]);

  const handleConnect = async () => {
    if (!user) return;

    setup.clearError();
    setConnecting(true);
    try {
      const token = await user.getIdToken();

      // Request a signed initiation URL via POST (token in header, not URL)
      const res = await fetch('/api/integrations/gsc/start', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ returnTo: integrationSetupReturnPath('gsc', setup.returnTo, isAttuneDesktopShell()) }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to start OAuth flow');
      }

      const { initiateUrl } = await res.json();

      // Navigate to the signed initiation URL (redirects to Google)
      window.location.href = initiateUrl;
    } catch (error) {
      console.error('Failed to start OAuth:', error);
      setup.reportError('Failed to start connection. Please try again.');
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!user) return;
    if (
      !confirm(
        'Are you sure you want to disconnect Google Search Console? This will remove all GSC data from your Pages.'
      )
    )
      return;

    setDisconnecting(true);
    try {
      const token = await user.getIdToken();
      const res = await fetch('/api/integrations/gsc/disconnect', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.ok) {
        toast.success('Google Search Console disconnected.');
        setStatus(null);
        fetchStatus();
      } else {
        throw new Error('Failed to disconnect');
      }
    } catch (error) {
      console.error('Failed to disconnect:', error);
      setup.reportError('Failed to disconnect. Please try again.');
    } finally {
      setDisconnecting(false);
    }
  };

  const handleOpenPropertyPicker = () => {
    if (!status?.canSelectProperties) {
      void handleConnect();
      return;
    }
    setShowPropertyPicker(true);
    fetchProperties();
  };

  const handleSelectProperty = async (siteUrl: string) => {
    if (!user || selectingProperty) return;
    const selectedDomain =
      brandDomain ||
      (siteUrl.startsWith('sc-domain:') ? siteUrl.slice(10) : new URL(siteUrl).hostname);

    setup.clearError();
    setSelectingProperty(siteUrl);
    try {
      const token = await user.getIdToken();
      const res = await fetch('/api/integrations/gsc/select-property', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          siteUrl,
          brandDomain: selectedDomain,
          projectId: currentProject?.id,
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        if (data.syncWarning || data.initialSync?.status === 'failed') {
          toast.warning(
            data.syncWarning ||
              data.initialSync?.errorMessage ||
              'Property linked, but initial sync failed.'
          );
        } else {
          toast.success('Property linked successfully!');
        }
        setShowPropertyPicker(false);
        await fetchStatus();
        setup.onConfigured();
      } else {
        throw new Error(data.error || 'Failed to link property');
      }
    } catch (error) {
      console.error('Failed to select property:', error);
      setup.reportError(error instanceof Error ? error.message : 'Failed to link property.');
    } finally {
      setSelectingProperty(null);
    }
  };

  const formatPropertyUrl = (siteUrl: string) => {
    if (siteUrl.startsWith('sc-domain:')) {
      return siteUrl.replace('sc-domain:', '');
    }
    try {
      return new URL(siteUrl).hostname;
    } catch {
      return siteUrl;
    }
  };

  const linkedProperty = status?.properties?.[0];
  const connected = Boolean(status?.connected);
  const connectedAt = status?.connection?.connectedAt
    ? new Date(status.connection.connectedAt).toLocaleDateString()
    : null;
  const statusTone = status?.needsReconnect ? 'warning' : connected ? 'connected' : 'idle';

  if (loading) {
    return (
      <IntegrationCard
        icon={<Link2 size={18} />}
        title="Google Search Console"
        description="Clicks, impressions, and linked property data for Pages."
        statusLabel="Loading"
        statusTone="idle"
      >
        <IntegrationSetupError message={setup.error} />
        <IntegrationStatusRow icon={<InlineSpinner size="xs" />} title="Loading status" />
      </IntegrationCard>
    );
  }

  return (
    <>
      <IntegrationCard
        icon={<Link2 size={18} />}
        title="Google Search Console"
        description="Clicks, impressions, and linked property data for Pages."
        statusLabel={
          connected ? (status?.needsReconnect ? 'Reconnect' : 'Connected') : 'Not connected'
        }
        statusTone={statusTone}
        action={
          connected && status?.canManageConnection ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDisconnect}
              disabled={disconnecting}
              color="ui.text.tertiary"
              _hover={{ color: 'status.danger.fg', bg: 'ui.surface.card' }}
            >
              {disconnecting ? <InlineSpinner size="xs" /> : <Unlink size={14} />}
              <Text ml={1}>Disconnect</Text>
            </Button>
          ) : connected && status?.canSelectProperties ? null : (
            <Button
              bg="accent.solid"
              color="accent.contrast"
              _hover={{ bg: 'accent.emphasized' }}
              borderRadius="glassButton"
              size="sm"
              onClick={handleConnect}
              disabled={connecting}
            >
              {connecting ? <InlineSpinner size="xs" /> : <ExternalLink size={14} />}
              <Text ml={2}>
                {connecting
                  ? 'Connecting...'
                  : linkedProperty
                    ? 'Connect your Google account'
                    : 'Connect'}
              </Text>
            </Button>
          )
        }
      >
        <IntegrationSetupError message={setup.error} />
        {connected || linkedProperty ? (
          <>
            <IntegrationStatusRow
              icon={<Check size={16} />}
              title={status?.connection?.googleEmail || 'Google account'}
              description={
                !status?.canManageConnection
                  ? 'Shared with this workspace by another manager'
                  : connectedAt
                    ? `Connected ${connectedAt}`
                    : 'Connected'
              }
            />
            {linkedProperty ? (
              <IntegrationStatusRow
                icon={<Link2 size={16} />}
                title={formatPropertyUrl(linkedProperty.siteUrl)}
                description={
                  <>
                    {linkedProperty.siteUrl.startsWith('sc-domain:')
                      ? 'Domain property'
                      : 'URL prefix property'}
                    {linkedProperty.lastSyncAt &&
                      ` - Last synced ${new Date(linkedProperty.lastSyncAt).toLocaleDateString()}`}
                  </>
                }
                action={
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleOpenPropertyPicker}
                    color="ui.text.secondary"
                    _hover={{ bg: 'ui.surface.card', color: 'ui.text.primary' }}
                  >
                    <RefreshCw size={14} />
                    <Text ml={1}>Change</Text>
                  </Button>
                }
              />
            ) : (
              <IntegrationStatusRow
                icon={<AlertCircle size={16} />}
                title="No property linked"
                description="Select a property to show Search Console data on Pages."
                action={
                  <Button
                    size="sm"
                    bg="accent.solid"
                    color="accent.contrast"
                    _hover={{ bg: 'accent.emphasized' }}
                    borderRadius="glassButton"
                    onClick={handleOpenPropertyPicker}
                  >
                    <Link2 size={14} />
                    <Text ml={1}>Link Property</Text>
                  </Button>
                }
              />
            )}
            {status?.needsReconnect && status.canManageConnection && (
              <IntegrationNotice
                icon={<AlertCircle size={16} color="var(--chakra-colors-orange-500)" />}
                action={
                  <Button size="sm" colorPalette="orange" variant="solid" onClick={handleConnect}>
                    Reconnect
                  </Button>
                }
              >
                Renew this connection to continue syncing data.
              </IntegrationNotice>
            )}
          </>
        ) : (
          <IntegrationStatusRow
            icon={<Link2 size={16} />}
            title="Connect Search Console"
            description="Pull search performance data into Pages."
          />
        )}
      </IntegrationCard>

      {/* Property Picker Dialog */}
      <IntegrationResourcePicker
        open={showPropertyPicker}
        busy={Boolean(selectingProperty)}
        onOpenChange={setShowPropertyPicker}
        title="Select Search Console property"
      >
        {loadingProperties ? (
          <HStack justify="center" py="8">
            <InlineSpinner size="md" />
            <Text color="ui.text.secondary">Loading properties…</Text>
          </HStack>
        ) : properties.length === 0 ? (
          <Box py="8" textAlign="center">
            <Text color="ui.text.secondary" mb="2">
              No Search Console properties found.
            </Text>
            <Text fontSize="sm" color="ui.text.tertiary">
              Make sure you have verified properties in Google Search Console.
            </Text>
          </Box>
        ) : (
          <VStack align="stretch" gap={2}>
            <Text fontSize="sm" color="ui.text.secondary" mb="2">
              {brandDomain ? (
                <>
                  Select the property for <strong>{brandDomain}</strong>.
                </>
              ) : (
                'Select the property to use in this workspace.'
              )}
            </Text>
            {properties.map(prop => {
              const isMatch =
                brandDomain &&
                (prop.siteUrl.includes(brandDomain) ||
                  prop.siteUrl.replace('sc-domain:', '') === brandDomain);

              return (
                <Button
                  key={prop.siteUrl}
                  type="button"
                  variant="plain"
                  w="full"
                  h="auto"
                  p="4"
                  bg={isMatch ? 'status.success.bg' : 'ui.surface.input'}
                  borderRadius="glass"
                  borderWidth="1px"
                  borderStyle="solid"
                  borderColor={isMatch ? 'status.success.border' : 'ui.border.subtle'}
                  justifyContent="space-between"
                  alignItems="center"
                  _hover={{ borderColor: 'accent.solid' }}
                  _focusVisible={{ outline: '2px solid', outlineColor: 'accent.solid' }}
                  whiteSpace="normal"
                  textAlign="left"
                  disabled={Boolean(selectingProperty)}
                  aria-pressed={prop.isSelected}
                  aria-busy={selectingProperty === prop.siteUrl}
                  aria-label={`${prop.isSelected ? 'Linked' : 'Select'} Search Console property ${formatPropertyUrl(prop.siteUrl)}`}
                  onClick={() => handleSelectProperty(prop.siteUrl)}
                >
                  <HStack gap={3}>
                    <Box>
                      <HStack gap={2}>
                        <Text fontWeight="500" color="ui.text.primary" fontSize="sm">
                          {formatPropertyUrl(prop.siteUrl)}
                        </Text>
                        {isMatch && (
                          <Badge colorPalette="green" size="sm">
                            Recommended
                          </Badge>
                        )}
                      </HStack>
                      <HStack gap={2} mt="1">
                        <Badge size="sm" variant="subtle">
                          {prop.siteUrl.startsWith('sc-domain:') ? 'Domain' : 'URL Prefix'}
                        </Badge>
                        <Text fontSize="xs" color="ui.text.tertiary">
                          {prop.permissionLevel}
                        </Text>
                      </HStack>
                    </Box>
                  </HStack>
                  <HStack
                    flexShrink={0}
                    px="3"
                    py="2"
                    bg={prop.isSelected ? 'status.success.fg' : undefined}
                    color={prop.isSelected ? 'white' : 'ui.text.primary'}
                    borderWidth="1px"
                    borderStyle="solid"
                    borderColor="ui.border.default"
                    borderRadius="glassButton"
                  >
                    {selectingProperty === prop.siteUrl ? (
                      <>
                        <InlineSpinner size="xs" />
                        <Text fontSize="sm">Linking…</Text>
                      </>
                    ) : prop.isSelected ? (
                      <>
                        <Check size={14} />
                        <Text fontSize="sm">Linked</Text>
                      </>
                    ) : (
                      <Text fontSize="sm">Select</Text>
                    )}
                  </HStack>
                </Button>
              );
            })}
          </VStack>
        )}
      </IntegrationResourcePicker>
    </>
  );
}
